import { Injectable } from '@angular/core';

import {
  catchError,
  EMPTY,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  Observable,
} from 'rxjs';
import { intervalBackoff } from 'backoff-rxjs';

import { ApiResponseModelWrapper } from '@tablesready/common-ui';
import { BrowserDate } from '@tablesready/server-time';
import { ApiClientService } from '@tablesready/api-client';

@Injectable({ providedIn: 'root' })
export class ServerTimeService {
  private readonly interval = 1000;
  private readonly url = '/public/servertime';

  constructor(private readonly http: ApiClientService) { }

  // It is important to use real browser time to calculate offset correctly
  private readonly serverTime = intervalBackoff(this.interval).pipe(
    startWith(0),
    map(() => BrowserDate.now()),
    switchMap(sentTime => this.getServerTime().pipe(
      map(serverTime => ({
        sentTime,
        serverTime,
        receivedTime: BrowserDate.now(),
      })),
      // A failed sample (offline, API down) must not error the shared stream for the rest
      // of the session; skip it and let the next backoff tick try again.
      catchError(() => EMPTY),
    )),
  );

  offset$ = this.serverTime.pipe(
    map(times => (2 * times.serverTime - times.sentTime - times.receivedTime) / 2),
    scan((acc, val) => [...acc, val].slice(-5), []),
    map(offsets => this.onServerTime(offsets)),
    shareReplay(1),
  );

  private getServerTime(): Observable<number> {
    return this.http
      .get<ApiResponseModelWrapper<number>>(this.url)
      .pipe(map(({ success, data }): number => {
        if (success) {
          return data;
        }
        throw new Error('error getting server time');
      }));
  }

  private onServerTime(data): number {
    let values = [...data];
    values.sort((a, b) => b - a);
    // Take middle three
    const middleValues = 3;
    if (values.length > middleValues) {
      values = values.slice(1, 1 + middleValues);
    }
    // Find average
    let sum = 0;
    for (const i of values) {
      sum += i;
    }

    return Math.floor(sum / values.length);
  }
}
